/**
 * **What day the tenant was built for**, read from the seed rather than guessed
 * from the browser clock.
 *
 * The store used to take its demo day from `new Date()`, so a tenant seeded the
 * night before disagreed with it by one day — and that one day cost us the
 * patient page, the episode plan reading differently from My week, the drag
 * guardrails reporting no compliance checks, and the referral panel twice: once
 * hiding a real failure behind a wrong answer, once asking about the wrong
 * 48 hours. Every one of them looked like a different bug.
 *
 * The seed writes its own demo day into the description of a service called
 * **Demo day** (Pete, 2026-09-24). A service attached to no visit type is
 * invisible in the product — the scheduler builds its picker from
 * `visitType.visitTypeServices` — while the GraphQL `service` root still returns
 * it, so the horizon query asks for it by name on every poll.
 *
 * **The marker's row id makes it a stamp.** The seed deletes and re-inserts the
 * marker every time it is applied, so the id changes with every reseed, the same
 * day seeded twice included. The store keeps the stamp it was built for and
 * rebuilds itself when the tenant's differs (`data/HorizonFetcher.tsx`), which is
 * what retired the Reset store step after a reseed.
 */
import { dateKeyAt, formatInBranchZone, isWorkingDayKey, nextWorkingDayKey, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import type { DateKey } from "@caredna-marlin/utils/homeHealthDemo/state";

export const DEMO_DAY_SERVICE = "Demo day";

interface ServiceLike {
  id?: number | string;
  name: string;
  description?: string | null;
}

type Services = Record<string, ServiceLike | undefined> | ServiceLike[] | null | undefined;

function marker(services: Services): ServiceLike | undefined {
  const list = Array.isArray(services) ? services : Object.values(services ?? {});
  return list.find((s) => s?.name === DEMO_DAY_SERVICE);
}

/** The tenant's own demo day, or undefined for a tenant seeded before this existed. */
export function tenantDemoDay(services: Services): DateKey | undefined {
  const day = marker(services)?.description?.trim();
  return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : undefined;
}

export interface TenantSeed {
  day: DateKey;
  /** Changes with every reseed: the marker row's id and its day. */
  stamp: string;
}

/**
 * The tenant's demo day and the stamp of the seed that wrote it, or undefined
 * when there is no marker — a tenant seeded before the marker existed, or one
 * whose seed is still being applied. Either way, no information: the store keeps
 * what it has.
 */
export function tenantSeed(services: Services): TenantSeed | undefined {
  const day = tenantDemoDay(services);
  const id = marker(services)?.id;
  return day && id !== undefined ? { day, stamp: `${id}:${day}` } : undefined;
}

/**
 * **The day Reset database offers first**: today in the branch zone, or the next
 * working day when today is a weekend (decided 2026-10-09; the seed is for
 * weekdays only).
 */
export function defaultResetDay(wallNow: Date, timeZone: string): DateKey {
  const today = toDateKey(wallNow, timeZone);
  return isWorkingDayKey(today) ? today : nextWorkingDayKey(today);
}

/**
 * **The demo time Reset database offers**, `HH:mm` in the branch zone: now on
 * today, and 08:00 on a later day, before that day has started, so a reset the
 * night before does not mark the whole day done. The presenter sets the hour
 * the demo will run instead, for a reset made hours ahead of it.
 */
export function defaultDemoTime(demoDay: DateKey, wallNow: Date, timeZone: string): string {
  return demoDay > toDateKey(wallNow, timeZone) ? "08:00" : formatInBranchZone(wallNow, timeZone, "HH:mm");
}

/**
 * **The instant a reset seeds "as of"**: the demo time on the demo day, in the
 * branch zone. Today's visits before it read complete, in progress, arrived or
 * en route, as Copy demo data marks them; the rest are scheduled.
 */
export function resetAsOf(demoDay: DateKey, demoTime: string, timeZone: string): Date {
  return dateKeyAt(demoDay, demoTime, timeZone);
}
